import { useMemo, useCallback } from 'react';
import {
  DomainUser,
  LocationACL,
  ResourceGrant,
  ResourceProfile,
} from '@raydiant/api-client-js';
import UserWithAccess from './UserWithAccess';
import Text from '@raydiant/raydial/components/Text';
import { GrantOption } from './DeviceGrantSelect';

interface UsersWithAccessProps {
  owner: ResourceProfile;
  contentType: string;
  grantOptions?: GrantOption[];
  locationACLs: LocationACL[];
  domainUsers: DomainUser[];
  onUpdate: (acl: LocationACL, grant: ResourceGrant) => void;
  onRemove: (acl: LocationACL) => void;
}

const UsersWithAccess = ({
  owner,
  contentType,
  grantOptions,
  locationACLs,
  domainUsers,
  onUpdate,
  onRemove,
}: UsersWithAccessProps) => {
  const superAdminUsers = useMemo(
    () => domainUsers.filter((user) => user.domainRole === 'superadmin'),
    [domainUsers],
  );
  const adminUsers = useMemo(
    () => domainUsers.filter((user) => user.domainRole === 'admin'),
    [domainUsers],
  );

  const domainAccess = useMemo(
    () => locationACLs.find((locationACL) => locationACL.grantDomainId),
    [locationACLs],
  );
  const userAccess = useMemo(
    () =>
      locationACLs.filter(
        (locationACL) =>
          !locationACL.grantDomainId && !locationACL.grantFolderPath,
      ),
    [locationACLs],
  );

  const aclWithAdminRoles = useMemo(
    (): LocationACL[] => [
      {
        id: 'superadmin',
        grantProfile: {
          id: 'superadmin',
          name: 'Super Admins',
          email: '',
          thumbnailUrl: '',
          domainId: '',
          domainRole: 'superadmin',
        },
        grantDomainId: null,
        grantScope: null,
        grantFolderPath: null,
        readGrant: true,
        updateGrant: true,
        deleteGrant: true,
        customAccessGrants: ['publish'],
        shareGrant: true,
        fullGrant: false,
      },
      {
        id: 'admin',
        grantProfile: {
          id: 'admin',
          name: 'Admins',
          email: '',
          thumbnailUrl: '',
          domainId: '',
          domainRole: 'admin',
        },
        grantDomainId: null,
        grantScope: null,
        grantFolderPath: null,
        readGrant: true,
        updateGrant: true,
        deleteGrant: false,
        customAccessGrants: ['publish'],
        shareGrant: false,
        fullGrant: false,
      },
      {
        id: 'owner',
        grantProfile: {
          id: owner?.id || '',
          name: owner?.name || '',
          email: owner?.email || '',
          thumbnailUrl: owner?.thumbnailUrl || '',
          domainId: owner?.domainId || '',
          domainRole: owner?.domainRole || 'restricted',
        },
        grantDomainId: '',
        grantScope: null,
        grantFolderPath: null,
        readGrant: true,
        updateGrant: true,
        deleteGrant: true,
        customAccessGrants: ['publish'],
        shareGrant: true,
        fullGrant: true,
      },
      ...(domainAccess ? [domainAccess] : []),
      ...userAccess,
    ],
    [owner, domainAccess, userAccess],
  );

  const renderToggleList = useCallback(
    (id: string) => {
      switch (id) {
        case 'superadmin':
          return superAdminUsers;
        case 'admin':
          return adminUsers;
        default:
          return [];
      }
    },
    [superAdminUsers, adminUsers],
  );

  return (
    <div>
      <div className="mb-2">
        <Text variant="smallSubheader" className="ml-4">
          Users with Access
        </Text>
      </div>
      <div className="max-h-[256px] overflow-x-hidden overflow-y-auto">
        {aclWithAdminRoles.map((locationACL, i) => (
          <UserWithAccess
            key={locationACL.id}
            contentType={contentType}
            acl={locationACL}
            grantOptions={grantOptions}
            avatarStyle={i + 3}
            showToggle={
              locationACL.id === 'superadmin' || locationACL.id === 'admin'
            }
            isOwner={locationACL.grantProfile?.id === owner.id}
            toggleList={renderToggleList(locationACL.id)}
            onUpdate={onUpdate}
            onRemove={onRemove}
          />
        ))}
      </div>
    </div>
  );
};

export default UsersWithAccess;
